import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import useFeedback from '../hooks/useFeedback';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Grid,
  Alert,
  CircularProgress,
  Divider,
  MenuItem,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Tooltip,
} from '@mui/material';
import {
  Save as SaveIcon,
  Schedule as ScheduleIcon,
  AccessTime as ClockIcon,
  Warning as WarningIcon,
  Timer as TimerIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  DateRange as DateRangeIcon,
  LocationOn as LocationIcon,
} from '@mui/icons-material';
import { getShiftSettings, updateShiftSettings, getAllPayPeriodPresets, createPayPeriodPreset, updatePayPeriodPreset, deletePayPeriodPreset } from '../api';
import { formatTimeDisplay } from '../utils/formatTime';
import { PERIOD_TYPES } from '../utils/payPeriodUtils';
import { useConfirmDialog } from './ConfirmDialog';

export default function ShiftConfiguration() {
  const { isAdmin } = useAuth();
  const [settings, setSettings] = useState({
    minimum_entry_minutes: 0,
    default_start_time: '09:00',
    default_end_time: '17:00',
    default_break_minutes: 0,
    require_short_entry_approval: true,
    time_rounding_minutes: 0,
    time_format: '12h',
    restrict_entry_creation_by_ip: false,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, showSuccess, clearSuccess] = useFeedback(5000);

  // Pay Period Presets state
  const [presets, setPresets] = useState([]);
  const [presetDialogOpen, setPresetDialogOpen] = useState(false);
  const [presetEditMode, setPresetEditMode] = useState(false);
  const [presetEditId, setPresetEditId] = useState(null);
  const [presetForm, setPresetForm] = useState({
    name: '', description: '', period_type: 'current_week', sort_order: 0, is_active: true, include_unpaid_previous: false,
  });

  const { showConfirm, ConfirmDialogComponent } = useConfirmDialog();

  useEffect(() => {
    loadSettings();
    if (isAdmin) loadPresets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const res = await getShiftSettings();
      if (res.data) {
        setSettings({
          minimum_entry_minutes: res.data.minimum_entry_minutes ?? 0,
          default_start_time: res.data.default_start_time || '09:00',
          default_end_time: res.data.default_end_time || '17:00',
          default_break_minutes: res.data.default_break_minutes ?? 0,
          require_short_entry_approval: res.data.require_short_entry_approval !== false,
          time_rounding_minutes: res.data.time_rounding_minutes ?? 0,
          time_format: res.data.time_format || '12h',
          restrict_entry_creation_by_ip: res.data.restrict_entry_creation_by_ip || false,
        });
      }
    } catch (err) {
      setError('Failed to load shift settings');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadPresets = async () => {
    try {
      const res = await getAllPayPeriodPresets();
      setPresets(res.data || []);
    } catch (err) {
      console.error('Failed to load presets:', err);
    }
  };

  const handleOpenPresetDialog = (preset = null) => {
    if (preset) {
      setPresetEditMode(true);
      setPresetEditId(preset.id);
      setPresetForm({
        name: preset.name,
        description: preset.description || '',
        period_type: preset.period_type,
        sort_order: preset.sort_order,
        is_active: preset.is_active,
        include_unpaid_previous: !!preset.include_unpaid_previous,
      });
    } else {
      setPresetEditMode(false);
      setPresetEditId(null);
      setPresetForm({
        name: '',
        description: '',
        period_type: 'current_week',
        sort_order: presets.length,
        is_active: true,
        include_unpaid_previous: false,
      });
    }
    setPresetDialogOpen(true);
  };

  const handleSavePreset = async () => {
    if (!presetForm.name) { setError('Preset name is required'); return; }
    try {
      if (presetEditMode && presetEditId) {
        await updatePayPeriodPreset(presetEditId, presetForm);
      } else {
        await createPayPeriodPreset(presetForm);
      }
      setPresetDialogOpen(false);
      showSuccess(presetEditMode ? 'Preset updated' : 'Preset created');
      loadPresets();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save preset');
    }
  };

  const handleDeletePreset = async (preset) => {
    const confirmed = await showConfirm({
      title: 'Delete Preset',
      message: `Are you sure you want to delete "${preset.name}"? This action cannot be undone.`,
      confirmText: 'Delete',
      confirmColor: 'error',
      severity: 'warning',
    });
    if (!confirmed) return;
    try {
      await deletePayPeriodPreset(preset.id);
      showSuccess('Preset deleted');
      loadPresets();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete preset');
    }
  };

  const handleTogglePresetActive = async (preset) => {
    try {
      await updatePayPeriodPreset(preset.id, { is_active: !preset.is_active });
      loadPresets();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update preset');
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setSettings(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    clearSuccess();
    
    try {
      await updateShiftSettings(settings);
      showSuccess('Shift configuration saved successfully');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 3, display: 'flex', alignItems: 'center' }}>
        <ScheduleIcon sx={{ mr: 1 }} />
        Shift Configuration
      </Typography>
      
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={clearSuccess}>{success}</Alert>}

      <Grid container spacing={3}>
        {/* Minimum Entry Threshold */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
              <WarningIcon sx={{ mr: 1, color: 'warning.main' }} />
              Minimum Entry Threshold
            </Typography>
            
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Set a minimum duration for time entries. Entries below this threshold cannot be submitted for approval.
            </Typography>

            <TextField
              select
              fullWidth
              label="Minimum Entry Duration"
              name="minimum_entry_minutes"
              value={settings.minimum_entry_minutes}
              onChange={handleChange}
              sx={{ mb: 2 }}
            >
              <MenuItem value={0}>Disabled (no minimum)</MenuItem>
              <MenuItem value={15}>15 minutes</MenuItem>
              <MenuItem value={30}>30 minutes</MenuItem>
              <MenuItem value={60}>60 minutes (1 hour)</MenuItem>
            </TextField>

            <FormControlLabel
              control={
                <Switch
                  checked={settings.require_short_entry_approval}
                  onChange={handleChange}
                  name="require_short_entry_approval"
                  disabled={settings.minimum_entry_minutes === 0}
                />
              }
              label="Require approval for entries below threshold"
            />
            
            {settings.minimum_entry_minutes > 0 && (
              <Alert severity="info" sx={{ mt: 2 }}>
                Time entries under {settings.minimum_entry_minutes} minutes cannot be submitted for approval and will show a warning.
              </Alert>
            )}
          </Paper>
        </Grid>

        {/* Time Rounding */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
              <TimerIcon sx={{ mr: 1, color: 'info.main' }} />
              Time Rounding
            </Typography>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Automatically round time entries to the nearest interval. Applied when creating or updating entries.
            </Typography>

            <TextField
              select
              fullWidth
              label="Round to Nearest"
              name="time_rounding_minutes"
              value={settings.time_rounding_minutes}
              onChange={handleChange}
              sx={{ mb: 2 }}
            >
              <MenuItem value={0}>None (exact time)</MenuItem>
              <MenuItem value={5}>Nearest 5 minutes</MenuItem>
              <MenuItem value={6}>Nearest 6 minutes (1/10 hour)</MenuItem>
              <MenuItem value={10}>Nearest 10 minutes</MenuItem>
              <MenuItem value={15}>Nearest 15 minutes (1/4 hour)</MenuItem>
              <MenuItem value={30}>Nearest 30 minutes (1/2 hour)</MenuItem>
              <MenuItem value={60}>Nearest 60 minutes (1 hour)</MenuItem>
            </TextField>

            {settings.time_rounding_minutes > 0 && (
              <Alert severity="info" sx={{ mt: 1 }}>
                Worked time will be rounded to the nearest {settings.time_rounding_minutes} minutes before calculating pay.
              </Alert>
            )}
          </Paper>
        </Grid>

        {/* Time Display Format */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
              <ClockIcon sx={{ mr: 1, color: 'secondary.main' }} />
              Time Display Format
            </Typography>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Choose how times are displayed throughout the application. This affects all users in your organization.
            </Typography>

            <TextField
              select
              fullWidth
              label="Time Display Format"
              name="time_format"
              value={settings.time_format}
              onChange={handleChange}
              sx={{ mb: 2 }}
            >
              <MenuItem value="12h">12-Hour (AM/PM) &mdash; e.g. 5:00 PM</MenuItem>
              <MenuItem value="24h">24-Hour (Military) &mdash; e.g. 17:00</MenuItem>
            </TextField>

            <Alert severity="info" sx={{ mt: 1 }}>
              Preview: {formatTimeDisplay('09:00', settings.time_format)} - {formatTimeDisplay('17:00', settings.time_format)}
            </Alert>
          </Paper>
        </Grid>

        {/* On-Site Entry Restriction */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
              <LocationIcon sx={{ mr: 1, color: 'warning.main' }} />
              On-Site Entry Restriction
            </Typography>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              When enabled, employees can only create new time entries or clock in when connected to an authorized work site network. Admins and managers are not restricted.
            </Typography>

            <FormControlLabel
              control={
                <Switch
                  checked={settings.restrict_entry_creation_by_ip}
                  onChange={handleChange}
                  name="restrict_entry_creation_by_ip"
                />
              }
              label="Require on-site network for new time entries"
            />

            {settings.restrict_entry_creation_by_ip && (
              <Alert severity="info" sx={{ mt: 2 }}>
                Employees must be connected to a network matching their assigned work site IP rules. Configure IP rules under Settings &gt; Work Sites.
              </Alert>
            )}
          </Paper>
        </Grid>

        {/* Default Shift Times */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
              <ClockIcon sx={{ mr: 1, color: 'primary.main' }} />
              Default Shift Times
            </Typography>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Set default times that will be pre-filled when creating new time entries.
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  type="time"
                  label="Default Start Time"
                  name="default_start_time"
                  value={settings.default_start_time}
                  onChange={handleChange}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  type="time"
                  label="Default End Time"
                  name="default_end_time"
                  value={settings.default_end_time}
                  onChange={handleChange}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  type="number"
                  label="Default Break (minutes)"
                  name="default_break_minutes"
                  value={settings.default_break_minutes}
                  onChange={handleChange}
                  inputProps={{ min: 0, max: 120 }}
                  helperText="0-120 minutes"
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Summary Card */}
        <Grid item xs={12}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Current Configuration Summary
              </Typography>
              <Divider sx={{ my: 1 }} />
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <Typography variant="body2" color="text.secondary">Minimum Entry:</Typography>
                  <Typography variant="body1">
                    {settings.minimum_entry_minutes === 0 
                      ? 'No minimum (disabled)' 
                      : `${settings.minimum_entry_minutes} minutes`}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="body2" color="text.secondary">Default Shift:</Typography>
                  <Typography variant="body1">
                    {formatTimeDisplay(settings.default_start_time, settings.time_format)} - {formatTimeDisplay(settings.default_end_time, settings.time_format)}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Typography variant="body2" color="text.secondary">Default Break:</Typography>
                  <Typography variant="body1">
                    {settings.default_break_minutes} minutes
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Typography variant="body2" color="text.secondary">Time Rounding:</Typography>
                  <Typography variant="body1">
                    {settings.time_rounding_minutes === 0
                      ? 'None (exact time)'
                      : `Nearest ${settings.time_rounding_minutes} min`}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Typography variant="body2" color="text.secondary">Time Format:</Typography>
                  <Typography variant="body1">
                    {settings.time_format === '24h' ? '24-Hour (Military)' : '12-Hour (AM/PM)'}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Box sx={{ mt: 3, mb: 4 }}>
        <Button
          variant="contained"
          size="large"
          startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
          onClick={handleSave}
          disabled={saving}
        >
          Save Configuration
        </Button>
      </Box>

      {/* Pay Period Presets Section - Admin only */}
      {isAdmin && (<>
      <Divider sx={{ my: 3 }} />
      <Typography variant="h5" sx={{ mb: 3, display: 'flex', alignItems: 'center' }}>
        <DateRangeIcon sx={{ mr: 1 }} />
        Pay Period Presets
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Configure the pay period options available in dropdown menus across the application (Timesheets, Reports).
      </Typography>

      <Paper sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="subtitle1" fontWeight="bold">
            Presets ({presets.length})
          </Typography>
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => handleOpenPresetDialog()}
          >
            Add Preset
          </Button>
        </Box>

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Order</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Period Type</TableCell>
                <TableCell align="center">Active</TableCell>
                <TableCell align="center">+ Unpaid</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {presets.map((preset) => (
                <TableRow key={preset.id} sx={{ opacity: preset.is_active ? 1 : 0.5 }}>
                  <TableCell>{preset.sort_order}</TableCell>
                  <TableCell>{preset.name}</TableCell>
                  <TableCell>
                    <Tooltip title={preset.description || ''}>
                      <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                        {preset.description || '—'}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={PERIOD_TYPES.find(pt => pt.value === preset.period_type)?.label || preset.period_type}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Switch
                      size="small"
                      checked={preset.is_active}
                      onChange={() => handleTogglePresetActive(preset)}
                    />
                  </TableCell>
                  <TableCell align="center">
                    {preset.include_unpaid_previous && (
                      <Chip label="Yes" size="small" color="info" variant="outlined" />
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <IconButton size="small" onClick={() => handleOpenPresetDialog(preset)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" color="error" onClick={() => handleDeletePreset(preset)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {presets.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                      No presets configured. Click "Add Preset" to create one.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Preset Add/Edit Dialog */}
      <Dialog open={presetDialogOpen} onClose={() => setPresetDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{presetEditMode ? 'Edit Preset' : 'Add Preset'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="Name"
                value={presetForm.name}
                onChange={(e) => setPresetForm(prev => ({ ...prev, name: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description (shown as tooltip)"
                value={presetForm.description}
                onChange={(e) => setPresetForm(prev => ({ ...prev, description: e.target.value }))}
                multiline
                rows={2}
              />
            </Grid>
            <Grid item xs={12} sm={8}>
              <TextField
                fullWidth
                select
                required
                label="Period Type"
                value={presetForm.period_type}
                onChange={(e) => setPresetForm(prev => ({ ...prev, period_type: e.target.value }))}
              >
                {PERIOD_TYPES.map(pt => (
                  <MenuItem key={pt.value} value={pt.value}>{pt.label}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                type="number"
                label="Sort Order"
                value={presetForm.sort_order}
                onChange={(e) => setPresetForm(prev => ({ ...prev, sort_order: parseInt(e.target.value) || 0 }))}
                inputProps={{ min: 0 }}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={presetForm.is_active}
                    onChange={(e) => setPresetForm(prev => ({ ...prev, is_active: e.target.checked }))}
                  />
                }
                label="Active (visible in dropdowns)"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={presetForm.include_unpaid_previous}
                    onChange={(e) => setPresetForm(prev => ({ ...prev, include_unpaid_previous: e.target.checked }))}
                  />
                }
                label="Include unpaid previous balances"
              />
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 6 }}>
                When enabled, also shows unpaid entries from before the period start date.
              </Typography>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setPresetDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSavePreset}>
            {presetEditMode ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
      </>)}

      {/* Confirm Dialog */}
      <ConfirmDialogComponent />
    </Box>
  );
}
